import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import { ConnectionHandler, type RecordSourceSelectorProxy } from 'relay-runtime';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { useExperienceFail } from '@atlassian/jira-experience-tracker/src/ui/experience-fail/index.tsx';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useExperienceSuccess } from '@atlassian/jira-experience-tracker/src/ui/experience-success/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { useSetSelectedGroupingOldMutation } from '@atlassian/jira-relay/src/__generated__/useSetSelectedGroupingOldMutation.graphql';
import type { useSetSelectedGroupingMutation } from '@atlassian/jira-relay/src/__generated__/useSetSelectedGroupingMutation.graphql';
import RelayDataID from '@atlassian/relay-data-id';
import {
	PACKAGE_NAME,
	TEAM_NAME,
	GROUP_BY_DROPDOWN_CHANGED_EXPERIENCE,
	ANALYTICS_SOURCE,
} from '../../common/constants.tsx';

const recentlyUsedConnectionUpdater = (
	store: RecordSourceSelectorProxy,
	recentlyUsedConnectionId: string,
	fieldId: string,
) => {
	const recentlyUsedConnection = store.get(recentlyUsedConnectionId);
	if (!recentlyUsedConnection) {
		return;
	}

	const fieldDataId = RelayDataID({ id: fieldId }, 'JiraField');
	if (!fieldDataId) {
		return;
	}

	const fieldNode = store.get(fieldDataId);
	if (!fieldNode) {
		return;
	}

	ConnectionHandler.deleteNode(recentlyUsedConnection, fieldDataId);

	const newEdge = ConnectionHandler.createEdge(
		store,
		recentlyUsedConnection,
		fieldNode,
		'JiraSpreadsheetGroupByFieldOptionEdge',
	);

	ConnectionHandler.insertEdgeBefore(recentlyUsedConnection, newEdge);

	// Ensure the list doesn't exceed 5 items
	const updatedEdges = recentlyUsedConnection.getLinkedRecords('edges') || [];
	if (updatedEdges.length > 5) {
		const lastEdge = updatedEdges[updatedEdges.length - 1];
		const lastNode = lastEdge?.getLinkedRecord('node');
		const lastNodeId = lastNode?.getDataID();
		if (typeof lastNodeId === 'string') {
			ConnectionHandler.deleteNode(recentlyUsedConnection, lastNodeId);
		}
	}
};

export type SetSelectedGroupingArgs = {
	fieldId: string | null;
	fieldName: string | null;
	viewId: string;
	onUpdateGroupByFieldAndRefetch: (props: { fieldId: string; name: string } | null) => void;
	/** If passed, the recently used connection will be updated with the selected field */
	recentlyUsedConnectionId?: string;
};

export const useSetSelectedGrouping = () => {
	const [commitOld] = useMutation<useSetSelectedGroupingOldMutation>(graphql`
		mutation useSetSelectedGroupingOldMutation($viewId: ID!, $fieldId: String) {
			jira {
				updateIssueSearchGroupByConfig(viewId: $viewId, fieldId: $fieldId)
					@optIn(to: "JiraUpdateIssueSearchGroupByFieldPreference") {
					success
					errors {
						message
					}
				}
			}
		}
	`);

	const [commit] = useMutation<useSetSelectedGroupingMutation>(graphql`
		mutation useSetSelectedGroupingMutation($input: JiraSetIssueSearchGroupByInput!) {
			jira_setIssueSearchGroupBy(input: $input) {
				success
				errors {
					message
				}
			}
		}
	`);

	const onExperienceStart = useExperienceStart({
		experience: GROUP_BY_DROPDOWN_CHANGED_EXPERIENCE,
		analyticsSource: ANALYTICS_SOURCE,
	});

	const onExperienceFail = useExperienceFail({
		experience: GROUP_BY_DROPDOWN_CHANGED_EXPERIENCE,
	});

	const onExperienceSuccess = useExperienceSuccess({
		experience: GROUP_BY_DROPDOWN_CHANGED_EXPERIENCE,
	});

	const handleError = useCallback(
		(error: Error) => {
			fireErrorAnalytics({
				meta: {
					id: 'issueTableGroupByDropdownMutation',
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});

			onExperienceFail(PACKAGE_NAME, error);
		},
		[onExperienceFail],
	);

	return useCallback(
		({
			fieldId,
			fieldName,
			viewId,
			onUpdateGroupByFieldAndRefetch,
			recentlyUsedConnectionId,
		}: SetSelectedGroupingArgs) => {
			onExperienceStart();

			if (fg('saved_list_view_technical_changes')) {
				commit({
					variables: {
						input: {
							viewId,
							fieldId,
						},
					},
					updater: (store) => {
						if (recentlyUsedConnectionId && fieldId) {
							recentlyUsedConnectionUpdater(store, recentlyUsedConnectionId, fieldId);
						}
					},
					onCompleted: (payload) => {
						if (payload?.jira_setIssueSearchGroupBy?.success) {
							onExperienceSuccess();
						} else {
							const error = new Error(
								payload?.jira_setIssueSearchGroupBy?.errors?.[0]?.message ??
									'Failed to update group by field: Unknown Error occurred',
							);
							handleError(error);
						}
					},
					onError: (error) => {
						handleError(error);
					},
				});
				// refetch query optimistically
				onUpdateGroupByFieldAndRefetch(fieldId && fieldName ? { fieldId, name: fieldName } : null);
			} else {
				commitOld({
					variables: { viewId, fieldId },
					updater: (store) => {
						if (recentlyUsedConnectionId && fieldId) {
							recentlyUsedConnectionUpdater(store, recentlyUsedConnectionId, fieldId);
						}
					},
					onCompleted: (payload) => {
						if (payload?.jira?.updateIssueSearchGroupByConfig?.success) {
							onExperienceSuccess();
						} else {
							const error = new Error(
								payload?.jira?.updateIssueSearchGroupByConfig?.errors?.[0]?.message ??
									'Failed to update group by field: Unknown Error occurred',
							);
							handleError(error);
						}
					},
					onError: (error) => {
						handleError(error);
					},
				});

				onUpdateGroupByFieldAndRefetch(fieldId && fieldName ? { fieldId, name: fieldName } : null);
			}
		},
		[onExperienceStart, commit, onExperienceSuccess, handleError, commitOld],
	);
};
